import { useEffect, useMemo, useRef, useState } from 'react';
import { legalDocuments, type LegalTab } from '@/lib/auth-legal';

interface AuthLegalConsentProps {
  checked: boolean;
  onChange: (value: boolean) => void;
  compact?: boolean;
}

const AuthLegalConsent = ({ checked, onChange, compact = false }: AuthLegalConsentProps) => {
  const [open, setOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<LegalTab>('terms');
  const [hasReadToBottom, setHasReadToBottom] = useState(false);
  const contentRef = useRef<HTMLDivElement | null>(null);

  const activeDocument = useMemo(
    () => legalDocuments.find((document) => document.key === activeTab) ?? legalDocuments[0],
    [activeTab]
  );

  useEffect(() => {
    if (!open) {
      return;
    }
    setHasReadToBottom(false);
    requestAnimationFrame(() => {
      if (contentRef.current) {
        contentRef.current.scrollTop = 0;
      }
    });
  }, [activeTab, open]);

  const handleScroll = () => {
    const element = contentRef.current;
    if (!element) {
      return;
    }
    const reachedBottom = element.scrollTop + element.clientHeight >= element.scrollHeight - 24;
    if (reachedBottom) {
      setHasReadToBottom(true);
    }
  };

  const confirmAgreement = () => {
    onChange(true);
    setOpen(false);
  };

  return (
    <>
      <div className={`flex items-start gap-2.5 rounded-xl border border-slate-200 bg-slate-50/80 px-3.5 py-3 ${compact ? 'mt-1' : ''}`}>
        <input
          type="checkbox"
          id="auth-legal-consent"
          checked={checked}
          onChange={(event) => onChange(event.target.checked)}
          className="mt-0.5 h-4 w-4 flex-shrink-0 rounded border-slate-300 accent-violet-600"
        />
        <label htmlFor="auth-legal-consent" className="text-xs leading-6 text-slate-600">
          我已閱讀並同意
          {' '}
          <button
            type="button"
            onClick={() => {
              setActiveTab('terms');
              setOpen(true);
            }}
            className="font-semibold text-violet-600 underline underline-offset-2 hover:text-violet-700"
          >
            服務條款
          </button>
          、{' '}
          <button
            type="button"
            onClick={() => {
              setActiveTab('privacy');
              setOpen(true);
            }}
            className="font-semibold text-violet-600 underline underline-offset-2 hover:text-violet-700"
          >
            私隱政策
          </button>
          {' '}及{' '}
          <button
            type="button"
            onClick={() => {
              setActiveTab('refund');
              setOpen(true);
            }}
            className="font-semibold text-violet-600 underline underline-offset-2 hover:text-violet-700"
          >
            退款政策
          </button>
        </label>
      </div>

      {open && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/50 px-4 py-6 backdrop-blur-sm">
          <div className="relative w-full max-w-4xl overflow-hidden rounded-[28px] border border-slate-200 bg-white shadow-[0_30px_100px_-20px_rgba(15,23,42,0.45)]">
            <button
              type="button"
              onClick={() => setOpen(false)}
              className="absolute right-5 top-5 z-10 flex h-9 w-9 items-center justify-center rounded-full bg-slate-100 text-slate-500 transition-colors hover:bg-slate-200 hover:text-slate-700"
            >
              <i className="ri-close-line text-lg" />
            </button>

            <div className="border-b border-slate-100 px-6 pb-5 pt-6 md:px-8">
              <div className="mb-4 pr-10">
                <p className="text-xs font-semibold uppercase tracking-[0.24em] text-violet-500">Legal Agreements</p>
                <h2 className="mt-2 text-2xl font-bold text-slate-900">{activeDocument.title}</h2>
                <p className="mt-1 text-sm text-slate-500">{activeDocument.subtitle}</p>
              </div>
              <div className="flex flex-wrap gap-2">
                {legalDocuments.map((document) => (
                  <button
                    key={document.key}
                    type="button"
                    onClick={() => setActiveTab(document.key)}
                    className={`rounded-full px-4 py-2 text-sm font-medium transition-colors ${
                      activeTab === document.key
                        ? 'bg-violet-600 text-white'
                        : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                    }`}
                  >
                    {document.title}
                  </button>
                ))}
              </div>
            </div>

            <div
              ref={contentRef}
              onScroll={handleScroll}
              className="max-h-[55vh] overflow-y-auto px-6 py-6 md:px-8"
            >
              <div className="space-y-6">
                {activeDocument.sections.map((section) => (
                  <section key={section.title} className="rounded-2xl border border-slate-100 bg-slate-50/70 p-5">
                    <h3 className="text-base font-semibold text-slate-900">{section.title}</h3>
                    {section.paragraphs?.map((paragraph) => (
                      <p key={paragraph} className="mt-3 text-sm leading-7 text-slate-600">
                        {paragraph}
                      </p>
                    ))}
                    {section.bullets && (
                      <div className="mt-3 space-y-2.5">
                        {section.bullets.map((bullet) => (
                          <div key={bullet} className="flex items-start gap-3 text-sm leading-7 text-slate-600">
                            <span className="mt-2 h-1.5 w-1.5 flex-shrink-0 rounded-full bg-violet-500" />
                            <span>{bullet}</span>
                          </div>
                        ))}
                      </div>
                    )}
                  </section>
                ))}
              </div>
            </div>

            <div className="flex flex-col gap-3 border-t border-slate-100 bg-white px-6 py-5 md:flex-row md:items-center md:justify-between md:px-8">
              <p className="text-xs leading-6 text-slate-500">
                請先閱讀至底部再確認同意，確認後會同步勾選此授權。
              </p>
              <button
                type="button"
                onClick={confirmAgreement}
                disabled={!hasReadToBottom}
                className="inline-flex items-center justify-center gap-2 rounded-xl bg-violet-600 px-5 py-3 text-sm font-semibold text-white transition-colors hover:bg-violet-700 disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-500"
              >
                <i className={hasReadToBottom ? 'ri-check-double-line' : 'ri-arrow-down-line'} />
                {hasReadToBottom ? '我已閱讀並同意' : '請先閱讀到最後'}
              </button>
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default AuthLegalConsent;
